import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Shield, User, AlertTriangle, CheckCircle, ExternalLink, GitBranch, Inbox, Send } from 'lucide-react';
import { listCatalogReviewQueueOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Callout, Card, EmptyState } from '../../../components/ui/index.js';
import type { DaemonReviewQueueItem } from '../../../services/api';
import { useDaemonScope } from './DaemonScopeContext.js';
import { daemonReads } from './daemon-queries.js';
import type { DaemonRecord } from '../../../generated/api/types.gen.js';

interface DaemonSupervisorPanelProps {
  daemon: DaemonRecord;
}

export const DaemonSupervisorPanel: React.FC<DaemonSupervisorPanelProps> = ({ daemon }) => {
  const { scope, basePath, entityLabel } = useDaemonScope();
  const { data: allDaemonsData, isError: allDaemonsError } = useQuery(daemonReads(scope).list());
  const {
    data: reviewQueueData,
    isLoading: reviewQueueLoading,
    isError: reviewQueueIsError,
  } = useQuery({ ...listCatalogReviewQueueOptions({ query: { limit: '500' } }), enabled: scope === 'system' });

  const allDaemons: DaemonRecord[] = allDaemonsData?.data ?? [];
  const reviewQueue = reviewQueueData?.data ?? [];
  const supervisor = daemon.supervisorDaemonId ? allDaemons.find((d) => d.id === daemon.supervisorDaemonId) : null;
  const supervised = allDaemons.filter((d) => d.supervisorDaemonId === daemon.id);
  const targetedReviewItems = reviewQueue.filter((item) => isTargetReviewItem(item, daemon));
  const requestedReviewItems = reviewQueue.filter((item) => item.sourceEntityId === daemon.id);

  const hasProbation = !!daemon.probationUntil;
  const probationDate = daemon.probationUntil ? new Date(daemon.probationUntil) : null;
  const probationPast = probationDate ? probationDate < new Date() : false;
  const hasDirectRelationships = !!daemon.supervisorDaemonId || supervised.length > 0 || hasProbation;
  const hasReviewRelationships = targetedReviewItems.length > 0 || requestedReviewItems.length > 0;

  if (
    !hasDirectRelationships &&
    !hasReviewRelationships &&
    !reviewQueueLoading &&
    !reviewQueueIsError &&
    !allDaemonsError
  ) {
    return (
      <Card>
        <EmptyState
          icon={Shield}
          title="No supervision relationships"
          description={`This ${entityLabel} has no supervisor, supervised ${entityLabel}s or probation.`}
        />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {allDaemonsError && (
        <Callout tone="danger">
          Could not load the {entityLabel} roster, so supervision relationships may be incomplete.
        </Callout>
      )}

      {/* Supervised By */}
      <Card>
        <h3 className="text-sm font-medium text-heading mb-3 flex items-center gap-2">
          <Shield size={14} className="text-accent-text" />
          Supervised By
        </h3>
        {supervisor ? (
          <Link
            to={`${basePath}/${supervisor.id}`}
            className="flex items-center gap-2 text-sm text-accent-text hover:underline"
          >
            <ExternalLink size={12} />
            {supervisor.name}
          </Link>
        ) : daemon.supervisorDaemonId ? (
          <span className="text-sm text-muted">Daemon #{daemon.supervisorDaemonId} (not found)</span>
        ) : (
          <Badge variant="neutral" size="md" icon={<User size={12} />}>
            Human review
          </Badge>
        )}
      </Card>

      {/* Supervises */}
      {supervised.length > 0 && (
        <Card>
          <h3 className="text-sm font-medium text-heading mb-3 flex items-center gap-2">
            <Shield size={14} className="text-success" />
            Supervises
            <Badge variant="neutral" size="sm">
              {supervised.length}
            </Badge>
          </h3>
          <div className="space-y-2">
            {supervised.map((d) => (
              <Link
                key={d.id}
                to={`${basePath}/${d.id}`}
                className="flex items-center justify-between p-2.5 rounded-lg bg-surface-alt hover:bg-surface-hover transition"
              >
                <div>
                  <span className="text-sm text-heading font-medium">{d.name}</span>
                  {d.department && <span className="ml-2 text-xs text-muted">{d.department}</span>}
                </div>
                {d.qualityScore != null && (
                  <Badge
                    variant={
                      parseFloat(d.qualityScore) >= 0.7
                        ? 'success'
                        : parseFloat(d.qualityScore) >= 0.4
                          ? 'warning'
                          : 'danger'
                    }
                    size="sm"
                  >
                    {(parseFloat(d.qualityScore) * 100).toFixed(0)}%
                  </Badge>
                )}
              </Link>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-medium text-heading flex items-center gap-2">
            <GitBranch size={14} className="text-accent-text" />
            Dynamic Review Relationships
          </h3>
          <Link
            to="/agents/libraries/catalog-review?tab=queue"
            className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline"
          >
            Catalog review queue <ExternalLink size={12} />
          </Link>
        </div>

        {reviewQueueLoading ? (
          <p className="mt-3 text-sm text-muted">Loading review relationships...</p>
        ) : reviewQueueIsError ? (
          <p className="mt-3 text-sm text-danger">Could not load review relationships.</p>
        ) : hasReviewRelationships ? (
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ReviewRelationshipCard
              icon={<Inbox size={14} className="text-accent-text" />}
              title="Review jobs received"
              description="Queue rows where this daemon is the active reviewer."
              items={targetedReviewItems}
              emptyLabel="No catalog review jobs currently target this daemon."
            />
            <ReviewRelationshipCard
              icon={<Send size={14} className="text-success" />}
              title="Reviews requested"
              description="Queue rows produced by this daemon's discoveries or findings."
              items={requestedReviewItems}
              emptyLabel="No downstream review jobs were created by this daemon."
            />
          </div>
        ) : (
          <p className="mt-3 text-sm text-muted">
            No catalog review queue rows currently link this daemon to another review daemon.
          </p>
        )}
      </Card>

      {/* Probation */}
      {hasProbation && (
        <Callout tone={probationPast ? 'success' : 'warning'} icon={probationPast ? CheckCircle : AlertTriangle}>
          {probationPast ? 'Probation completed' : `On probation until ${probationDate?.toLocaleDateString()}`}
        </Callout>
      )}
    </div>
  );
};

function isTargetReviewItem(item: DaemonReviewQueueItem, daemon: DaemonRecord) {
  if (item.targetDaemonId === daemon.id) return true;
  return Boolean(daemon.slug && item.targetDaemonName === daemon.slug);
}

function ReviewRelationshipCard({
  icon,
  title,
  description,
  items,
  emptyLabel,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  items: DaemonReviewQueueItem[];
  emptyLabel: string;
}) {
  const statusCounts = countByStatus(items);
  const latest = items[0];

  return (
    <div className="rounded-lg border bg-surface-alt p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-heading">
            {icon}
            {title}
          </div>
          <p className="mt-1 text-xs text-muted">{description}</p>
        </div>
        <Badge variant={items.length > 0 ? 'info' : 'neutral'} size="sm">
          {items.length}
        </Badge>
      </div>

      {items.length === 0 ? (
        <p className="mt-3 text-sm text-muted">{emptyLabel}</p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {Object.entries(statusCounts).map(([status, count]) => (
              <Badge key={status} variant={statusVariant(status)} size="sm">
                {status}: {count}
              </Badge>
            ))}
          </div>
          {latest && (
            <div className="mt-3 rounded-sm border bg-surface p-2 text-xs">
              <div className="font-medium text-body">{latest.eventType.replaceAll('_', ' ')}</div>
              <div className="mt-1 text-muted">
                Target: {latest.targetDaemonName}
                {latest.candidateTargetType ? ` / ${latest.candidateTargetType}` : ''}
                {latest.candidateStatus ? ` / ${latest.candidateStatus}` : ''}
              </div>
              {latest.candidateSummary && <p className="mt-1 text-muted">{latest.candidateSummary}</p>}
              {latest.lastError && <p className="mt-1 text-danger">{latest.lastError}</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function countByStatus(items: DaemonReviewQueueItem[]) {
  return items.reduce<Record<string, number>>((acc, item) => {
    acc[item.status] = (acc[item.status] ?? 0) + 1;
    return acc;
  }, {});
}

function statusVariant(status: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' {
  if (status === 'completed') return 'success';
  if (status === 'pending' || status === 'locked') return 'warning';
  if (status === 'failed' || status === 'blocked') return 'danger';
  return 'neutral';
}
